import React from 'react';
import styled from 'styled-components';

import { Label, Tooltip } from '@block-explorer/ui';

import {
  BodyText,
  DEFAULT_AMOUNT_PRECISION,
  FlexColumn,
  FlexRow,
  formatNumber,
} from '@make-software/cspr-ui';
import { useGetMetrics } from '../../hooks';
import { useTranslation } from 'react-i18next';
import Big from 'big.js';
import { useCurrencyRateAmount } from '../../hooks/event-store-hooks';
import FiatCurrency from '../../components/multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { InfoSvgIcon } from '../../components';

const Container = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: ['row'],
    justifyContent: ['flex-start'],
    width: '100%',
    '> div': {
      minWidth: '80px',
    },
    '> * + *': {
      marginLeft: [0, 16, 24, 32],
    },
  })
);

export const NavMetrics = React.memo(() => {
  const { data } = useGetMetrics();
  const { t } = useTranslation();
  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const csprCurrencyRate = rateData?.data?.amount?.toString();

  const apyPercentage =
    data?.totalSupply == null || data?.eraTotalStake == null
      ? null
      : Big(data.totalSupply)
          .mul(0.08)
          .div(data.eraTotalStake)
          .mul(100)
          .toString();

  const loading = data == null || rateData?.data == null || loadingRateData;

  return (
    <Container>
      <FlexColumn>
        <BodyText size={1} scale={'lg'} variation="white" loading={loading}>
          {data && formatNumber(data.blockHeight)}
        </BodyText>
        <Label variation="darkGray" transform={'uppercase'}>
          {t('Latest Block Height')}
        </Label>
      </FlexColumn>
      <FlexColumn>
        <BodyText size={1} scale={'lg'} variation="white" loading={loading}>
          {apyPercentage && formatNumber(apyPercentage, { precision: 2 })}%
        </BodyText>
        <FlexRow align={'center'}>
          <Label variation="darkGray" transform={'uppercase'}>
            {t('APY')}
          </Label>
          <Tooltip
            title={t(
              "Expected annual percentage yield on the staked tokens without taking the validator's fee into account"
            )}
          >
            <InfoSvgIcon
              src="assets/icons/ic-info.svg"
              style={{ marginLeft: 3 }}
              size={12}
            />
          </Tooltip>
        </FlexRow>
      </FlexColumn>
      <FlexColumn>
        <BodyText size={1} scale={'lg'} variation="white" loading={loading}>
          <FiatCurrency
            amount={csprCurrencyRate}
            precision={DEFAULT_AMOUNT_PRECISION}
          />
        </BodyText>
        <Label variation="darkGray" transform={'uppercase'}>
          {t('CSPR price')}
        </Label>
      </FlexColumn>
      <FlexColumn>
        <BodyText size={1} scale={'lg'} variation="white" loading={loading}>
          {data && formatNumber(data.circulatingSupply)}
        </BodyText>
        <Label variation="darkGray" transform={'uppercase'}>
          {t('Circulating Supply')}
        </Label>
      </FlexColumn>
    </Container>
  );
});
